import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  LineChart, Line, BarChart, Bar, ComposedChart, Area, AreaChart,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine
} from 'recharts';
import acoNames from '../data/aco_names.json';
import { fmt, COLORS } from '../utils';
import ChartCard from './ChartCard';
import { generateMSSPReport } from '../generateMSSPReport';
import KPITooltip, { TOOLTIPS } from './KPITooltip';

// ── CAHPS labels ──────────────────────────────────────────────────────────
const CAHPS_LABELS = {
  cahps_1: 'Getting Needed Care', cahps_2: 'Getting Care Quickly',
  cahps_3: 'How Well Doctors Communicate', cahps_4: "Patients' Rating of Doctor",
  cahps_5: 'Access to Specialists', cahps_6: 'Health Promotion & Education',
  cahps_7: 'Shared Decision Making', cahps_8: 'Health Status/Functional Status',
  cahps_9: 'Care Coordination', cahps_11: 'Courteous & Helpful Staff',
};

const CLINICAL_MEASURES_DD = {
  qualityid_318:   { label: 'Fall Risk Screening',             higher: true  },
  qualityid_110:   { label: 'Influenza Immunization',          higher: true  },
  qualityid_226:   { label: 'Tobacco Screening & Cessation',   higher: true  },
  qualityid_134_wi:{ label: 'Depression Screening',            higher: true  },
  qualityid_113:   { label: 'Colorectal Cancer Screening',     higher: true  },
  qualityid_112:   { label: 'Breast Cancer Screening',         higher: true  },
  qualityid_438:   { label: 'Statin Therapy (CV)',             higher: true  },
  qualityid_370:   { label: 'Depression Remission (12mo)',     higher: true  },
  qualityid_001_wi:{ label: 'Diabetes: HbA1c Poor Control',   higher: false },
  qualityid_236_wi:{ label: 'Controlling High Blood Pressure', higher: true  },
  measure_479:     { label: '30-Day Readmission Rate',         higher: false },
  measure_484:     { label: 'Admissions: Multiple Chronic',    higher: false },
};
const CLINICAL_MEASURE_YEARS_DD = {
  2021: ['qualityid_318','qualityid_110','qualityid_226','qualityid_134_wi','qualityid_113','qualityid_112','qualityid_438','qualityid_370','qualityid_001_wi','qualityid_236_wi','measure_479'],
  2022: ['qualityid_318','qualityid_110','qualityid_226','qualityid_134_wi','qualityid_113','qualityid_112','qualityid_438','qualityid_370','qualityid_001_wi','qualityid_236_wi','measure_479','measure_484'],
  2023: ['qualityid_318','qualityid_110','qualityid_226','qualityid_134_wi','qualityid_113','qualityid_112','qualityid_438','qualityid_370','qualityid_001_wi','qualityid_236_wi','measure_479','measure_484'],
};

// ── Search ────────────────────────────────────────────────────────────────
function ACOSearch({ onSelect, compact }) {
  const [query, setQuery] = useState('');
  const [focused, setFocused] = useState(false);
  const sortedACOs = useMemo(() => [...acoNames].sort((a, b) => a.aco_name.localeCompare(b.aco_name)), []);
  const results = useMemo(() => {
    if (!query.trim()) return sortedACOs.slice(0, 50);
    const q = query.toLowerCase();
    return sortedACOs.filter(a => a.aco_name?.toLowerCase().includes(q) || a.aco_id?.toLowerCase().includes(q)).slice(0, 50);
  }, [query, sortedACOs]);
  const handleSelect = (aco) => { setQuery(aco.aco_name); setFocused(false); onSelect(aco); };
  return (
    <div style={{ position: 'relative', maxWidth: compact ? 380 : 520, flex: compact ? 1 : undefined }}>
      <input type="text" value={query} onChange={e => setQuery(e.target.value)}
        onFocus={() => setFocused(true)} onBlur={() => setTimeout(() => setFocused(false), 180)}
        placeholder="Search ACO name or ID…"
        style={{ width: '100%', padding: '9px 13px', fontSize: 13, border: '1.5px solid var(--gray-300)',
          borderRadius: 'var(--radius)', outline: 'none', fontFamily: 'var(--font)' }} />
      {focused && results.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 99,
          background: 'white', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius)',
          boxShadow: 'var(--shadow-lg)', maxHeight: 300, overflowY: 'auto', marginTop: 4 }}>
          {results.map(a => (
            <div key={a.aco_id} onMouseDown={() => handleSelect(a)}
              style={{ padding: '9px 14px', cursor: 'pointer', borderBottom: '1px solid var(--gray-100)',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--blue-light)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
              <div>
                <div style={{ fontWeight: 500, fontSize: 13, color: 'var(--navy)' }}>{a.aco_name}</div>
                <div style={{ fontSize: 11, color: 'var(--gray-500)' }}>{a.aco_id} · {a.aco_state || 'N/A'} · PY {a.first_year}–{a.most_recent_year}</div>
              </div>
              <span style={{ fontSize: 11, background: 'var(--teal-light)', color: 'var(--teal-dark)',
                padding: '2px 8px', borderRadius: 12, fontWeight: 500, flexShrink: 0 }}>{a.years_count}yr</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Stat badge ────────────────────────────────────────────────────────────
function StatBadge({ label, value, sub, positive, negative, width }) {
  const color = positive ? 'var(--green)' : negative ? 'var(--red)' : 'var(--gray-700)';
  return (
    <div style={{ background: 'var(--gray-50)', border: '1px solid var(--gray-200)',
      borderRadius: 'var(--radius)', padding: '10px 14px', minWidth: width || 120 }}>
      <div style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 500,
        textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 17, fontWeight: 700, color, marginTop: 3 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

// ── Toggle group ──────────────────────────────────────────────────────────
function ToggleGroup({ value, options, onChange }) {
  return (
    <div style={{ display: 'flex', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)', overflow: 'hidden', flexShrink: 0 }}>
      {options.map(opt => (
        <button key={opt.value} onClick={() => onChange(opt.value)}
          style={{ padding: '4px 12px', fontSize: 12, fontFamily: 'var(--font)', cursor: 'pointer',
            border: 'none', borderRight: '1px solid var(--gray-200)',
            background: value === opt.value ? 'var(--navy)' : 'white',
            color: value === opt.value ? 'white' : 'var(--gray-600)',
            fontWeight: value === opt.value ? 600 : 400 }}>
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Tooltips ──────────────────────────────────────────────────────────────
const FinTooltip = ({ active, payload, label, mode }) => {
  if (!active || !payload?.length) return null;
  const fmtVal = (v, key) => {
    if (v == null) return '—';
    if (key?.includes('pct') || key === 'sav_rate_pct') return `${Number(v).toFixed(2)}%`;
    if (mode === 'pbpm') return `$${Number(v).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`;
    return fmt.dollars(v, 2);
  };
  return (
    <div className="custom-tooltip">
      <div className="custom-tooltip-title">PY {label}</div>
      {payload.map((p, i) => p.value != null && (
        <div className="custom-tooltip-row" key={i}>
          <span style={{ color: p.color || p.fill }}>{p.name}</span>
          <span className="custom-tooltip-val">{fmtVal(p.value, p.dataKey)}</span>
        </div>
      ))}
    </div>
  );
};

const UtilTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="custom-tooltip">
      <div className="custom-tooltip-title">PY {label}</div>
      {payload.map((p, i) => p.value != null && (
        <div className="custom-tooltip-row" key={i}>
          <span style={{ color: p.color }}>{p.name}</span>
          <span className="custom-tooltip-val">{Number(p.value).toFixed(1)} per 1K</span>
        </div>
      ))}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────
export default function ACODeepDive({ perfData, initialACO, initialVersion, pinnedACO, pinACO }) {
  const [selectedACO, setSelectedACO] = useState(null);
  const [reportStatus, setReportStatus] = useState(null);
  const [acoRecords, setAcoRecords]   = useState([]);
  const [spendMode, setSpendMode]     = useState('pbpm');   // aggregate | percapita | pbpm
  const [beneField, setBeneField]     = useState('total');  // total | esrd | dis | agdu | agnd
  const [provYear, setProvYear]       = useState(null);
  const [detailYear, setDetailYear]   = useState(null); // shared year for grouped section

  const handleSelect = useCallback((aco) => { setSelectedACO(aco); }, []);

  // Load initialACO from cross-page navigation — fires whenever version increments
  useEffect(() => {
    if (initialACO) setSelectedACO(initialACO);
  }, [initialACO, initialVersion]); // eslint-disable-line

  useEffect(() => {
    if (!selectedACO || !perfData) return;
    const records = perfData
      .filter(r => r.aco_id === selectedACO.aco_id && r.is_midyear === 0)
      .sort((a, b) => a.performance_year - b.performance_year);
    setAcoRecords(records);
    setProvYear(records[records.length - 1]?.performance_year || null);
    setDetailYear(records[records.length - 1]?.performance_year || null);
    setRvmPairIdx(null); // reset to most recent on ACO change
  }, [selectedACO, perfData]);

  // ── All-time aggregates ──
  const allTime = useMemo(() => {
    if (!acoRecords.length) return null;
    const totalEarned  = acoRecords.reduce((s,r) => s + (r.earn_save_loss||0), 0);
    const totalGen     = acoRecords.filter(r => r.gen_save_loss != null).reduce((s,r) => s + (r.gen_save_loss||0), 0);
    const yrsEarning   = acoRecords.filter(r => (r.earn_save_loss||0) > 0).length;
    const avgSavRate   = acoRecords.filter(r => r.sav_rate != null).reduce((s,r) => s + r.sav_rate, 0)
                         / acoRecords.filter(r => r.sav_rate != null).length;
    const avgQual      = acoRecords.filter(r => r.qual_score != null);
    const avgQ         = avgQual.length ? avgQual.reduce((s,r) => s + r.qual_score, 0) / avgQual.length : null;
    return { totalEarned, totalGen, yrsEarning, avgSavRate, avgQ, n: acoRecords.length };
  }, [acoRecords]);

  // ── Program averages by year (for comparator) ──
  const progAvg = useMemo(() => {
    if (!perfData) return {};
    const byYear = {};
    const years = [...new Set(acoRecords.map(r => r.performance_year))];
    years.forEach(yr => {
      const recs = perfData.filter(r => r.performance_year === yr && r.is_midyear === 0);
      const avg  = (arr, fn) => { const v = arr.map(fn).filter(x=>x!=null); return v.length ? v.reduce((s,x)=>s+x,0)/v.length : null; };
      byYear[yr] = {
        sav_rate:         avg(recs, r => r.sav_rate),
        qual_score:       avg(recs, r => r.qual_score),
        expense_pbpm:     avg(recs, r => r.expense_pbpm),
        composite_raf_py: avg(recs, r => r.composite_raf_py),
        earn_per_capita:  avg(recs, r => r.earn_per_capita),
        adm:              avg(recs, r => r.adm),
        p_edv_vis:        avg(recs, r => r.p_edv_vis),
        p_em_pcp_vis:     avg(recs, r => r.p_em_pcp_vis),
      };
    });
    return byYear;
  }, [perfData, acoRecords]);

  const handleExportReport = async () => {
    if (!selectedACO || !acoRecords.length) return;
    try {
      await generateMSSPReport(
        selectedACO, acoRecords, perfData,
        (msg) => setReportStatus(msg)
      );
      setReportStatus(null);
    } catch (err) {
      console.error('Report generation failed:', err);
      setReportStatus('Error — please try again');
      setTimeout(() => setReportStatus(null), 3000);
    }
  };

  // ── Chart data ──
  const chartData = useMemo(() => acoRecords.map(r => {
    const py  = progAvg[r.performance_year] || {};
    // Beneficiary series based on selected mode
    const beneMap = {
      total: r.n_ab_year_py, esrd: r.n_ab_year_esrd_py,
      dis: r.n_ab_year_dis_py, agdu: r.n_ab_year_aged_dual_py, agnd: r.n_ab_year_aged_nondual_py,
    };
    const rafMap = {
      total: r.composite_raf_py, esrd: r.cms_hcc_riskscore_esrd_py,
      dis: r.cms_hcc_riskscore_dis_py, agdu: r.cms_hcc_riskscore_agdu_py, agnd: r.cms_hcc_riskscore_agnd_py,
    };

    // Spend mode values
    const spendVals = {
      aggregate:  { bnchmk: r.abtot_bnchmk,         exp: r.abtot_exp,         earned: r.earn_save_loss },
      percapita:  { bnchmk: r.benchmark_per_capita,  exp: r.expense_per_capita, earned: r.earn_per_capita },
      pbpm:       { bnchmk: r.benchmark_pbpm,         exp: r.expense_pbpm,       earned: r.earn_pbpm },
    };
    const sv = spendVals[spendMode] || spendVals.pbpm;

    return {
      ...r,
      sav_rate_pct:        r.sav_rate != null ? r.sav_rate * 100 : null,
      earn_M:              r.earn_save_loss != null ? r.earn_save_loss / 1e6 : null,
      gen_M:               r.gen_save_loss  != null ? r.gen_save_loss  / 1e6 : null,
      bene_selected:       beneMap[beneField],
      raf_selected:        rafMap[beneField],
      // Spend mode
      sv_bnchmk: sv.bnchmk, sv_exp: sv.exp, sv_earned: sv.earned,
      // Gap (savings opportunity)
      sv_gap: sv.bnchmk != null && sv.exp != null ? sv.bnchmk - sv.exp : null,
      sv_gap_pct: sv.bnchmk ? ((sv.bnchmk - sv.exp) / sv.bnchmk * 100) : null,
      // Program averages
      prog_sav_rate_pct:   py.sav_rate != null ? py.sav_rate * 100 : null,
      prog_qual:           py.qual_score,
      prog_expense_pbpm:   py.expense_pbpm,
      prog_raf:            py.composite_raf_py,
      prog_earn_pc:        py.earn_per_capita,
      prog_adm:            py.adm,
      prog_edv:            py.p_edv_vis,
      prog_pcp_em:         py.p_em_pcp_vis,
      // Net ED
      net_ed: (r.p_edv_vis||0) - (r.p_edv_vis_hosp||0),
      prog_net_ed: py.p_edv_vis != null ? py.p_edv_vis * 0.7 : null, // approx
    };
  }), [acoRecords, spendMode, beneField, progAvg, detailYear]);

  // ── RVM: available year pairs ──
  const yearPairs = useMemo(() => {
    const pairs = [];
    for (let i = 1; i < acoRecords.length; i++) {
      pairs.push({ from: acoRecords[i-1].performance_year, to: acoRecords[i].performance_year, idx: i });
    }
    return pairs;
  }, [acoRecords]);

  const [rvmPairIdx, setRvmPairIdx] = useState(null); // null = most recent
  const rvmPair = useMemo(() => {
    const idx = rvmPairIdx !== null ? rvmPairIdx : yearPairs.length - 1;
    return yearPairs[idx] || null;
  }, [yearPairs, rvmPairIdx]);

  // ── RVM computation ──
  const rvmRows = useMemo(() => {
    if (!rvmPair) return [];
    const curr = acoRecords[rvmPair.idx];
    const prev = acoRecords[rvmPair.idx - 1];
    const py_c = curr.n_ab_year_py || 1;
    const py_p = prev.n_ab_year_py || 1;

    const rvm = (name, tier, u_c, u_p, cap_c, cap_p, mixRows) => {
      const cpe_c = (u_c > 0 && cap_c != null) ? cap_c / (u_c/1000) : null;
      const cpe_p = (u_p > 0 && cap_p != null) ? cap_p / (u_p/1000) : null;
      const total_c = (cap_c || 0) * py_c;
      const total_p = (cap_p || 0) * py_p;
      const total_d = total_c - total_p;
      let panel_eff = null, vol_eff = null, rate_eff = null;
      if (cap_p != null) panel_eff = cap_p * (py_c - py_p);
      if (u_c != null && u_p != null && cpe_p != null) vol_eff = (u_c - u_p)/1000 * cpe_p * py_p;
      if (cpe_c != null && cpe_p != null && u_c != null) rate_eff = (cpe_c - cpe_p) * (u_c/1000) * py_c;
      return {
        name, tier, u_c, u_p, cpe_c, cpe_p, cap_c, cap_p,
        total_d, panel_eff, vol_eff, rate_eff,
        mixRows: mixRows || [],
      };
    };

    const rows = [
      // Tier 1: Full RVM + mix
      rvm('Inpatient (Total)', 'full',
        curr.adm, prev.adm, curr.capann_inp_all, prev.capann_inp_all, [
          rvm('  Acute',  'mix', curr.adm_s_trm,  prev.adm_s_trm,  curr.capann_inp_s_trm,  prev.capann_inp_s_trm),
          rvm('  Rehab',  'mix', curr.adm_rehab,  prev.adm_rehab,  curr.capann_inp_rehab,  prev.capann_inp_rehab),
          rvm('  LTC',    'mix', curr.adm_l_trm,  prev.adm_l_trm,  curr.capann_inp_l_trm,  prev.capann_inp_l_trm),
          rvm('  Psych',  'mix', curr.adm_psych,  prev.adm_psych,  curr.capann_inp_psych,  prev.capann_inp_psych),
        ]
      ),
      rvm('SNF', 'full', curr.p_snf_adm, prev.p_snf_adm, curr.capann_snf, prev.capann_snf),
      // Tier 2: Volume only (no spend)
      rvm('ED Visits (non-hosp)', 'volume',
        (curr.p_edv_vis||0) - (curr.p_edv_vis_hosp||0),
        (prev.p_edv_vis||0) - (prev.p_edv_vis_hosp||0), null, null),
      rvm('PCP E&M Visits',       'volume', curr.p_em_pcp_vis, prev.p_em_pcp_vis, null, null),
      rvm('Specialist E&M Visits','volume', curr.p_em_sp_vis,  prev.p_em_sp_vis,  null, null),
      // Tier 3: Spend trend only (no util rate)
      rvm('Outpatient (OPD)',  'spend', null, null, curr.capann_opd,    prev.capann_opd),
      rvm('Physician/PB',     'spend', null, null, curr.capann_pb,     prev.capann_pb),
      rvm('Home Health',      'spend', null, null, curr.capann_hha,    prev.capann_hha),
      rvm('Hospice',          'spend', null, null, curr.capann_hsp,    prev.capann_hsp),
      rvm('Ambulatory',       'spend', null, null, curr.capann_ambpay, prev.capann_ambpay),
      rvm('DME',              'spend', null, null, curr.capann_dme,    prev.capann_dme),
    ];
    return rows;
  }, [rvmPair, acoRecords]);

  // ── Service mix comparison (ACO vs program) ──
  const mixData = useMemo(() => { // detailYear dependency added below
    const rec = acoRecords.find(r => r.performance_year === detailYear) || acoRecords[acoRecords.length - 1];
    const latest = rec; // use detailYear if selected
    if (!latest) return [];
    const cats = [
      { name: 'Inpatient',   acoVal: latest.capann_inp_all, field: 'capann_inp_all' },
      { name: 'Physician',   acoVal: latest.capann_pb,      field: 'capann_pb' },
      { name: 'Outpatient',  acoVal: latest.capann_opd,     field: 'capann_opd' },
      { name: 'SNF',         acoVal: latest.capann_snf,     field: 'capann_snf' },
      { name: 'Home Health', acoVal: latest.capann_hha,     field: 'capann_hha' },
      { name: 'Hospice',     acoVal: latest.capann_hsp,     field: 'capann_hsp' },
      { name: 'Amb/Other',   acoVal: latest.capann_ambpay,  field: 'capann_ambpay' },
      { name: 'DME',         acoVal: latest.capann_dme,     field: 'capann_dme' },
    ].filter(c => c.acoVal != null && c.acoVal > 0);
    // Program avg for latest year
    const yr = latest.performance_year;
    const progRecs = perfData?.filter(r => r.performance_year === yr && r.is_midyear === 0) || [];
    const acoTotal  = cats.reduce((s,c) => s + c.acoVal, 0);
    const result = cats.map(c => {
      const progVals = progRecs.map(r => r[c.field]).filter(v => v != null);
      const progAvgVal = progVals.length ? progVals.reduce((s,v) => s+v, 0)/progVals.length : null;
      const progTotal  = progRecs.reduce((s,r) => {
        return s + cats.reduce((ss, cc) => ss + (r[cc.field]||0), 0);
      }, 0) / (progRecs.length || 1);
      return {
        name: c.name,
        'ACO %':  acoTotal  > 0 ? parseFloat((c.acoVal / acoTotal * 100).toFixed(1)) : 0,
        'Prog %': progAvgVal != null && progTotal > 0 ? parseFloat((progAvgVal / (progRecs.reduce((s,r) => s + cats.reduce((ss,cc)=>ss+(r[cc.field]||0),0), 0)/progRecs.length) * 100).toFixed(1)) : 0,
        acoVal:   c.acoVal,
        progVal:  progAvgVal,
      };
    });
    return result;
  }, [acoRecords, perfData, detailYear]);

  // ── Quality comparison data ──
  const qualData = useMemo(() => {
    const rec = acoRecords.find(r => r.performance_year === detailYear) || acoRecords[acoRecords.length - 1];
    const latest = rec; // use detailYear if selected
    if (!latest) return [];
    const yr = latest.performance_year;
    const progRecs = perfData?.filter(r => r.performance_year === yr && r.is_midyear === 0) || [];
    const progAvgCahps = (field) => {
      const vals = progRecs.map(r => r[field]).filter(v=>v!=null);
      return vals.length ? vals.reduce((s,v)=>s+v,0)/vals.length : null;
    };
    return Object.entries(CAHPS_LABELS).map(([field, label]) => ({
      label: label.length > 22 ? label.slice(0,22)+'…' : label,
      fullLabel: label,
      aco:  latest[field],
      prog: progAvgCahps(field),
    })).filter(d => d.aco != null || d.prog != null);
  }, [acoRecords, perfData]);

  // Clinical quality measures ACO vs program avg (most recent year with data)
  const clinicalQualData = useMemo(() => {
    if (!acoRecords.length || !perfData) return [];
    // Find most recent year that has clinical measures
    const yearsWithData = Object.keys(CLINICAL_MEASURE_YEARS_DD).map(Number).sort((a,b)=>b-a);
    const acoYears = new Set(acoRecords.map(r => r.performance_year));
    const useYear = yearsWithData.find(yr => acoYears.has(yr));
    if (!useYear) return [];
    const acoRec = acoRecords.find(r => r.performance_year === useYear);
    if (!acoRec) return [];
    const progRecs = perfData.filter(r => r.performance_year === useYear && r.is_midyear === 0);
    const fields = CLINICAL_MEASURE_YEARS_DD[useYear] || [];
    return fields.map(field => {
      const meta = CLINICAL_MEASURES_DD[field];
      if (!meta) return null;
      const acoVal = acoRec[field] != null ? Number(acoRec[field]) : null;
      const progVals = progRecs.map(r => r[field]).filter(v => v != null && !isNaN(Number(v))).map(Number);
      const progAvgVal = progVals.length ? progVals.reduce((s,v)=>s+v,0)/progVals.length : null;
      return { field, label: meta.label, higher: meta.higher, aco: acoVal, prog: progAvgVal, year: useYear };
    }).filter(d => d && (d.aco != null || d.prog != null));
  }, [acoRecords, perfData]);

  const latest  = acoRecords[acoRecords.length - 1];
  const prior   = acoRecords[acoRecords.length - 2];
  // detailRec for single-year section — uses detailYear if available, else latest
  const detailRec = useMemo(() => {
    if (!detailYear) return latest;
    return acoRecords.find(r => r.performance_year === detailYear) || latest;
  }, [acoRecords, detailYear, latest]);
  const detailProgAvgData = detailYear ? (progAvg[detailYear] || {}) : {};


  // Spend mode label helpers
  const spendLabel = { aggregate: 'Aggregate ($)', percapita: 'Per Beneficiary', pbpm: 'PBPM' };

  const spendYFmt  = (v) => {
    if (v == null) return '';
    if (spendMode === 'aggregate') return `$${(v/1e9).toFixed(1)}B`;
    return `$${Math.round(v).toLocaleString()}`;
  };

  // ── Empty state ──
  if (!selectedACO) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        <div className="chart-card">
          <div className="chart-title" style={{ marginBottom: 16 }}>Search for an ACO</div>
          <ACOSearch onSelect={handleSelect} />
          <p style={{ marginTop: 16, fontSize: 13, color: 'var(--gray-500)' }}>
            Search any of the 1,038 ACOs that participated in the MSSP between 2013 and 2024.
          </p>
        </div>
        <div className="chart-card">
          <div className="chart-title" style={{ marginBottom: 12 }}>Long-tenured ACOs to explore</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {[...acoNames].filter(a => a.years_count >= 10).sort((a,b) => a.aco_name.localeCompare(b.aco_name)).map(a => (
              <button key={a.aco_id} onClick={() => handleSelect(a)}
                style={{ padding: '6px 12px', borderRadius: 20, border: '1px solid var(--gray-300)',
                  background: 'var(--gray-50)', cursor: 'pointer', fontSize: 12, color: 'var(--navy)',
                  fontFamily: 'var(--font)', fontWeight: 500 }}
                onMouseEnter={e => { e.target.style.background='var(--teal-light)'; e.target.style.borderColor='var(--teal)'; }}
                onMouseLeave={e => { e.target.style.background='var(--gray-50)'; e.target.style.borderColor='var(--gray-300)'; }}>
                {a.aco_name}
                <span style={{ color: 'var(--gray-400)', fontWeight: 400, marginLeft: 4 }}>·{a.years_count}yr</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── Header ── */}
      <div className="chart-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--gray-500)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{selectedACO.aco_id}</div>
            <h2 style={{ fontSize: '1.3rem', marginTop: 2 }}>{selectedACO.aco_name}</h2>
            <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 4, display: 'flex', gap: 16, flexWrap: 'wrap' }}>
              {selectedACO.aco_state && <span>📍 {selectedACO.aco_state}</span>}
              <span>📅 Since PY {selectedACO.first_year}</span>
              <span>🗓 {acoRecords.length} performance years</span>
              {latest?.current_track && <span>🏷 {latest.current_track} · {latest?.risk_model || '—'}</span>}
              {latest?.snf_waiver === 1 && <span>🏨 SNF Waiver</span>}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            {pinACO && (
              <button
                onClick={() => pinACO(pinnedACO?.aco_id === selectedACO.aco_id ? null : selectedACO)}
                style={{ padding: '7px 14px', borderRadius: 'var(--radius)', fontSize: 12,
                  fontFamily: 'var(--font)', cursor: 'pointer', fontWeight: 600,
                  border: '1.5px solid ' + (pinnedACO?.aco_id === selectedACO.aco_id ? 'var(--teal)' : 'var(--gray-300)'),
                  background: pinnedACO?.aco_id === selectedACO.aco_id ? 'var(--teal-light)' : 'white',
                  color: pinnedACO?.aco_id === selectedACO.aco_id ? 'var(--teal-dark)' : 'var(--gray-500)' }}>
                {pinnedACO?.aco_id === selectedACO.aco_id ? '📌 Pinned — click to unpin' : '📌 Pin across all pages'}
              </button>
            )}
            {selectedACO && (
              <button onClick={handleExportReport}
                disabled={!!reportStatus}
                style={{ padding:'7px 16px', borderRadius:'var(--radius)', fontSize:12,
                  fontFamily:'var(--font)', cursor: reportStatus ? 'default' : 'pointer',
                  fontWeight:600, border:'none',
                  background: reportStatus ? 'rgba(66,186,151,0.15)' : 'var(--teal)',
                  color: reportStatus ? 'var(--teal)' : 'var(--navy)',
                  minWidth:170, transition:'all 0.2s' }}>
                {reportStatus || '📄 Export Report →'}
              </button>
            )}
            <ACOSearch onSelect={handleSelect} compact />
            <button onClick={() => { setSelectedACO(null); setAcoRecords([]); }}
              style={{ padding: '9px 14px', borderRadius: 'var(--radius)', border: '1px solid var(--gray-300)',
                background: 'var(--gray-50)', cursor: 'pointer', fontSize: 12, color: 'var(--gray-600)', fontFamily: 'var(--font)' }}>
              Clear
            </button>
          </div>
        </div>

        {/* ── All-time aggregate strip ── */}
        {allTime && (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase',
              letterSpacing: '0.08em', marginTop: 20, marginBottom: 8 }}>All-Time · PY {selectedACO.first_year}–{selectedACO.most_recent_year}</div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <StatBadge label="Total Earned Savings"   value={fmt.dollars(allTime.totalEarned, 2)} positive={allTime.totalEarned > 0} />
              <StatBadge label="Total Generated Savings" value={fmt.dollars(allTime.totalGen, 2)} positive={allTime.totalGen > 0} />
              <StatBadge label="Years Earning Savings"  value={`${allTime.yrsEarning} of ${allTime.n}`} positive={allTime.yrsEarning === allTime.n} />
              <StatBadge label="Avg Savings Rate"       value={fmt.pct(allTime.avgSavRate)} positive={allTime.avgSavRate > 0} negative={allTime.avgSavRate < 0} />
              {allTime.avgQ != null && <StatBadge label="Avg Quality Score" value={allTime.avgQ.toFixed(1)} />}
            </div>
          </>
        )}

        {/* ── Most recent year KPIs ── */}
        {latest && (
          <>
            <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--gray-400)', textTransform: 'uppercase',
              letterSpacing: '0.08em', marginTop: 18, marginBottom: 8 }}>Most Recent Year · PY {latest.performance_year}</div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <StatBadge label={<KPITooltip {...TOOLTIPS.savings_rate} />} value={fmt.pct(latest.sav_rate)}
                sub={`Prog avg: ${fmt.pct(progAvg[latest.performance_year]?.sav_rate)}`}
                positive={latest.sav_rate > 0} negative={latest.sav_rate < 0} />
              <StatBadge label={<KPITooltip {...TOOLTIPS.earned_savings} />} value={fmt.dollars(latest.earn_save_loss, 2)}
                positive={latest.earn_save_loss > 0} negative={latest.earn_save_loss < 0} />
              <StatBadge label="Assigned Beneficiaries" value={fmt.num(latest.n_ab)} />
              <StatBadge label={<KPITooltip {...TOOLTIPS.qual_score} />} value={latest.qual_score != null ? latest.qual_score.toFixed(1) : '—'}
                sub={`Prog avg: ${progAvg[latest.performance_year]?.qual_score?.toFixed(1) ?? '—'}`} />
              <StatBadge label={<KPITooltip {...TOOLTIPS.pbpm} />}
                value={latest.expense_pbpm != null ? `$${Number(latest.expense_pbpm).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}` : '—'}
                sub={`Prog avg: $${Number(progAvg[latest.performance_year]?.expense_pbpm||0).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`} />
              <StatBadge label="Composite RAF" value={latest.composite_raf_py?.toFixed(3) ?? '—'}
                sub={`Prog avg: ${progAvg[latest.performance_year]?.composite_raf_py?.toFixed(3) ?? '—'}`} />
              {latest.earn_pbpm != null && prior?.earn_pbpm != null && (
                <StatBadge label="Earned PBPM vs Prior"
                  value={`${(latest.earn_pbpm - prior.earn_pbpm) > 0 ? '+' : ''}$${Number(latest.earn_pbpm - prior.earn_pbpm).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}`}
                  positive={latest.earn_pbpm > prior.earn_pbpm} negative={latest.earn_pbpm < prior.earn_pbpm} />
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Savings rate + earned vs generated ── */}
      <div className="chart-grid-2">
        <ChartCard title="Savings Rate Over Time"
          sub="ACO savings rate by performance year · positive = under benchmark"
          filename={`${selectedACO?.aco_id}-savings-rate`}>
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="savGradACO" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.teal} stopOpacity={0.25} />
                  <stop offset="95%" stopColor={COLORS.teal} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
              <XAxis dataKey="performance_year" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={v => `${v.toFixed(1)}%`} tick={{ fontSize: 11 }} width={46} />
              <Tooltip content={<FinTooltip />} />
              <ReferenceLine y={0} stroke={COLORS.gray} strokeWidth={1.5} />
              <Area dataKey="sav_rate_pct" name="ACO Savings Rate" stroke={COLORS.teal} strokeWidth={2.5}
                fill="url(#savGradACO)" dot={{ r: 3.5, fill: COLORS.teal, stroke: 'white', strokeWidth: 2 }} connectNulls />

            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>
        <ChartCard title="Earned vs. Generated Savings"
          sub="Generated = gross; Earned = after quality adjustment"
          filename={`${selectedACO?.aco_id}-earned-vs-generated`}>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
              <XAxis dataKey="performance_year" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={v => v >= 1e9 ? `$${(v/1e9).toFixed(1)}B` : v >= 1e6 ? `$${(v/1e6).toFixed(1)}M` : `$${(v/1e3).toFixed(0)}K`} tick={{ fontSize: 11 }} width={56} />
              <Tooltip content={<FinTooltip />} />
              <Legend iconType="square" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="gen_save_loss"  name="Generated" fill={COLORS.blue} radius={[3,3,0,0]} />
              <Bar dataKey="earn_save_loss" name="Earned"    fill={COLORS.teal} radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* ── Beneficiaries & RAF ── */}
      <div className="chart-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
          <div>
            <div className="chart-title">Assigned Beneficiaries & Risk Score</div>
            <div className="chart-sub">Panel size (left) vs. composite RAF score (right) · filter by enrollment type</div>
          </div>
          <ToggleGroup value={beneField} onChange={setBeneField} options={[
            { value: 'total', label: 'Total' }, { value: 'esrd', label: 'ESRD' },
            { value: 'dis', label: 'Disabled' }, { value: 'agdu', label: 'Aged Dual' },
            { value: 'agnd', label: 'Aged Non-Dual' },
          ]} />
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData} margin={{ top: 8, right: 52, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
            <XAxis dataKey="performance_year" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={52} tickFormatter={v => fmt.shortNum(v)} />
            <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={44} domain={['auto','auto']} tickFormatter={v => v?.toFixed(2)} />
            <Tooltip labelFormatter={l => `PY ${l}`}
              formatter={(v, name) => name.includes('RAF') ? [v?.toFixed(3), name] : [fmt.num(v), name]} />
            <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
            <Line yAxisId="left"  dataKey="bene_selected" name="Beneficiaries"
              stroke={COLORS.navy} strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
            <Line yAxisId="right" dataKey="raf_selected"  name="RAF Score"
              stroke={COLORS.amber} strokeWidth={2} strokeDasharray="5 2" dot={{ r: 3 }} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* ── Quality over time ── */}
      <div className="chart-card">
        <div className="chart-title">Quality Score vs. Program Average</div>
        <div className="chart-sub">ACO quality score vs. program average · 75 = minimum QPS threshold</div>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
            <XAxis dataKey="performance_year" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} domain={[50, 105]} width={36} />
            <Tooltip labelFormatter={l => `PY ${l}`} formatter={v => [v?.toFixed(1), '']} />
            <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
            <ReferenceLine y={75} stroke="var(--gray-300)" strokeDasharray="4 2"
              label={{ value: 'QPS threshold', position: 'insideTopRight', fontSize: 10, fill: 'var(--gray-400)' }} />
            <Line dataKey="qual_score" name="ACO Quality Score" stroke={COLORS.purple} strokeWidth={2.5}
              dot={{ r: 4, fill: COLORS.purple, stroke: 'white', strokeWidth: 2 }} connectNulls />
            <Line dataKey="prog_qual"  name="Program Average"   stroke={COLORS.gray}   strokeWidth={1.5}
              strokeDasharray="4 2" dot={false} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* ── Benchmark vs expense (reworked) + utilization ── */}
      <div className="chart-grid-2">
        <div className="chart-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4, flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div className="chart-title">Benchmark vs. Actual Expense</div>
              <div className="chart-sub">Shaded gap = savings opportunity · bars (right axis) = earned savings captured</div>
            </div>
            <ToggleGroup value={spendMode} onChange={setSpendMode} options={[
              { value: 'pbpm', label: 'PBPM' }, { value: 'percapita', label: 'Per Bene' },
              { value: 'aggregate', label: 'Total' },
            ]} />
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={chartData} margin={{ top: 8, right: 56, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="gapGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={COLORS.teal} stopOpacity={0.18} />
                  <stop offset="100%" stopColor={COLORS.teal} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
              <XAxis dataKey="performance_year" tick={{ fontSize: 11 }} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={60} tickFormatter={spendYFmt} domain={['auto','auto']} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} width={52}
                tickFormatter={spendYFmt} label={{ value: 'Earned', angle: 90, position: 'insideRight', fontSize: 10, fill: 'var(--gray-400)', offset: 10 }} />
              <Tooltip content={<FinTooltip mode={spendMode} />} />
              <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="right" dataKey="sv_earned" name={`Earned (${spendLabel[spendMode]})`}
                fill={COLORS.teal} opacity={0.6} radius={[3,3,0,0]} />
              <Area yAxisId="left" dataKey="sv_bnchmk" name="Benchmark" stroke={COLORS.navy}
                strokeWidth={2} strokeDasharray="6 3" fill="url(#gapGrad)" fillOpacity={1} dot={false} connectNulls />
              <Line yAxisId="left" dataKey="sv_exp" name="Actual Expense" stroke={COLORS.red}
                strokeWidth={2.5} dot={{ r: 3 }} connectNulls />
            </ComposedChart>
          </ResponsiveContainer>
          <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
            Left axis: benchmark & expense scale · Right axis: earned savings scale · Shaded = gap (savings opportunity)
          </div>
        </div>

        <div className="chart-card">
          <div className="chart-title">Utilization vs. Program Average</div>
          <div className="chart-sub">Per 1,000 beneficiary years · solid = ACO · dashed = program avg</div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
              <XAxis dataKey="performance_year" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} width={40} />
              <Tooltip content={<UtilTooltip />} />
              <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
              <Line dataKey="adm"      name="IP Admissions (ACO)"  stroke={COLORS.navy}  strokeWidth={2} dot={{ r: 3 }} connectNulls />
              <Line dataKey="prog_adm" name="IP Admissions (Prog)" stroke={COLORS.navy}  strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls />
              <Line dataKey="net_ed"   name="Net ED Visits (ACO)"  stroke={COLORS.coral} strokeWidth={2} dot={{ r: 3 }} connectNulls />
              <Line dataKey="prog_edv" name="ED Visits (Prog)"     stroke={COLORS.coral} strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* ── E&M visits ── */}
      <div className="chart-card">
        <div className="chart-title">E&M Visits by Provider Type</div>
        <div className="chart-sub">Per 1,000 beneficiary years — ACO (solid) vs. program average (dashed)</div>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={chartData} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--gray-200)" />
            <XAxis dataKey="performance_year" tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} width={50} />
            <Tooltip content={<UtilTooltip />} />
            <Legend iconType="circle" iconSize={10} wrapperStyle={{ fontSize: 12 }} />
            <Line dataKey="p_em_pcp_vis" name="PCP E&M (ACO)"     stroke={COLORS.teal}  strokeWidth={2} dot={{ r: 3 }} connectNulls />
            <Line dataKey="prog_pcp_em"  name="PCP E&M (Prog avg)" stroke={COLORS.teal}  strokeWidth={1.5} strokeDasharray="4 2" dot={false} connectNulls />
            <Line dataKey="p_em_sp_vis"  name="Specialist E&M"     stroke={COLORS.navy}  strokeWidth={2} dot={{ r: 3 }} connectNulls />
            <Line dataKey="p_nurse_vis"  name="APP/Nurse Visits"   stroke={COLORS.amber} strokeWidth={2} dot={{ r: 3 }} connectNulls />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* ── RVM Analysis Table ── */}
      {yearPairs.length > 0 && (
        <div className="chart-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, flexWrap: 'wrap', gap: 12 }}>
            <div>
              <div className="chart-title">Rate / Volume / Mix Analysis</div>
              <div className="chart-sub">
                Year-over-year expense change decomposed by service category.
                Panel = enrollment growth effect · Volume = events per bene change · Rate = cost per event change.
              </div>
              <div style={{ marginTop: 8, padding: '8px 12px', background: 'var(--blue-light)',
                borderRadius: 'var(--radius)', borderLeft: '3px solid var(--teal)', fontSize: 12, color: 'var(--navy)' }}>
                <strong>Why this matters:</strong> Most financial summaries show total expense change but not <em>why</em> it changed.
                This table separates three drivers — did costs rise because you served more patients (Panel),
                because each patient had more services (Volume), or because each service cost more (Rate)?
                That distinction shapes very different strategic responses.
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 12, color: 'var(--gray-500)' }}>Period:</span>
              <select value={rvmPairIdx !== null ? rvmPairIdx : yearPairs.length - 1}
                onChange={e => setRvmPairIdx(Number(e.target.value))}
                style={{ padding: '5px 10px', border: '1px solid var(--gray-300)', borderRadius: 'var(--radius)',
                  fontSize: 13, fontFamily: 'var(--font)', color: 'var(--gray-800)', background: 'white', cursor: 'pointer' }}>
                {yearPairs.map((p, i) => (
                  <option key={i} value={i}>{p.from} → {p.to}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Legend */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 12, flexWrap: 'wrap' }}>
            {[
              { color: COLORS.blue,  label: 'Panel effect (enrollment Δ)' },
              { color: COLORS.navy,  label: 'Volume effect (events/bene Δ)' },
              { color: COLORS.teal,  label: 'Rate effect (cost/event Δ)' },
              { color: COLORS.coral, label: 'Negative component' },
            ].map(l => (
              <div key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div style={{ width: 10, height: 10, borderRadius: 2, background: l.color }} />
                <span style={{ fontSize: 11, color: 'var(--gray-600)' }}>{l.label}</span>
              </div>
            ))}
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ background: 'var(--gray-50)', borderBottom: '2px solid var(--gray-200)' }}>
                  {['Category','Util Rate /1K','Cost / Event','Total Δ ($M)','Panel ($M)','Volume ($M)','Rate ($M)','Notes'].map(h => (
                    <th key={h} style={{ padding: '7px 10px', textAlign: h === 'Category' ? 'left' : 'right',
                      fontWeight: 600, color: 'var(--gray-600)', fontSize: 10,
                      textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rvmRows.map((row, ri) => {
                  const rows = [row];
                  if (row.mixRows?.length) rows.push(...row.mixRows);
                  return rows.map((r, si) => {
                    const isMain = si === 0;
                    const fmtM = (v) => v != null ? (
                      <span style={{ fontWeight: isMain ? 600 : 400, color: v > 0 ? 'var(--red)' : v < 0 ? 'var(--green)' : 'var(--gray-500)' }}>
                        {v > 0 ? '+' : ''}{v.toFixed(1)}
                      </span>
                    ) : <span style={{ color: 'var(--gray-300)' }}>—</span>;

                    const tier = r.tier;
                    const note = tier === 'volume' ? 'Volume only — no spend field in PUF'
                               : tier === 'spend'  ? 'Spend trend — no util rate in PUF'
                               : tier === 'mix'    ? 'Mix sub-type'
                               : '';

                    // Mini waterfall bar
                    const maxVal = Math.max(
                      Math.abs(r.total_d/1e6 || 0),
                      Math.abs(r.panel_eff/1e6 || 0),
                      Math.abs(r.vol_eff/1e6 || 0),
                      Math.abs(r.rate_eff/1e6 || 0),
                      0.1
                    );

                    return (
                      <tr key={`${ri}-${si}`} style={{
                        background: isMain && ri % 2 === 0 ? 'var(--gray-50)' : isMain ? 'white' : '#f8fbff',
                        borderBottom: '1px solid var(--gray-100)',
                      }}>
                        <td style={{ padding: '6px 10px', fontWeight: isMain ? 600 : 400,
                          color: isMain ? 'var(--navy)' : 'var(--gray-600)',
                          paddingLeft: r.tier === 'mix' ? 24 : 10 }}>
                          {r.name}
                        </td>
                        {/* Util rate */}
                        <td style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--gray-700)' }}>
                          {r.u_c != null && r.u_p != null ? (
                            <span>
                              {r.u_p?.toFixed(1)} → {r.u_c?.toFixed(1)}
                              <span style={{ fontSize: 10, color: r.u_c > r.u_p ? 'var(--red)' : 'var(--green)', marginLeft: 4 }}>
                                ({r.u_c > r.u_p ? '+' : ''}{(r.u_c - r.u_p)?.toFixed(1)})
                              </span>
                            </span>
                          ) : <span style={{ color: 'var(--gray-300)' }}>—</span>}
                        </td>
                        {/* Cost per event */}
                        <td style={{ padding: '6px 10px', textAlign: 'right', color: 'var(--gray-700)' }}>
                          {r.cpe_c != null && r.cpe_p != null ? (
                            <span>
                              ${Math.round(r.cpe_p).toLocaleString()} → ${Math.round(r.cpe_c).toLocaleString()}
                              <span style={{ fontSize: 10, color: r.cpe_c > r.cpe_p ? 'var(--red)' : 'var(--green)', marginLeft: 4 }}>
                                ({r.cpe_c > r.cpe_p ? '+' : ''}{Math.round(r.cpe_c - r.cpe_p).toLocaleString()})
                              </span>
                            </span>
                          ) : <span style={{ color: 'var(--gray-300)' }}>—</span>}
                        </td>
                        {/* Total delta with mini bar */}
                        <td style={{ padding: '6px 10px', textAlign: 'right' }}>
                          {r.total_d != null ? (
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                              {fmtM(r.total_d/1e6)}
                              <div style={{ width: 48, height: 4, background: 'var(--gray-100)', borderRadius: 2 }}>
                                <div style={{ width: `${Math.min(100, Math.abs(r.total_d/1e6)/maxVal*100)}%`, height: '100%', borderRadius: 2,
                                  background: r.total_d > 0 ? 'var(--red)' : 'var(--green)', marginLeft: r.total_d < 0 ? 'auto' : 0 }} />
                              </div>
                            </div>
                          ) : <span style={{ color: 'var(--gray-300)' }}>—</span>}
                        </td>
                        <td style={{ padding: '6px 10px', textAlign: 'right' }}>{fmtM(r.panel_eff != null ? r.panel_eff/1e6 : null)}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right' }}>{fmtM(r.vol_eff   != null ? r.vol_eff/1e6   : null)}</td>
                        <td style={{ padding: '6px 10px', textAlign: 'right' }}>{fmtM(r.rate_eff  != null ? r.rate_eff/1e6  : null)}</td>
                        <td style={{ padding: '6px 10px', fontSize: 11, color: 'var(--gray-400)' }}>{note}</td>
                      </tr>
                    );
                  });
                })}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: 12, fontSize: 11, color: 'var(--gray-400)', lineHeight: 1.5 }}>
            <strong>Panel</strong> = spend change attributable to enrollment growth (same rate &amp; vol as prior year, more benes) ·
            <strong> Volume</strong> = change in events per 1,000 bene-years × prior cost/event ·
            <strong> Rate</strong> = change in cost per event × current utilization rate ·
            Red = expense increase · Green = expense decrease
          </div>
        </div>
      )}

      {/* ── Single-Year Detail Group (shared year selector) ── */}
      {latest && (() => {
        return (
          <>
            {/* Shared year selector */}
            <div className="chart-card" style={{ padding: '12px 20px', background: 'var(--navy)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'white' }}>Single-Year Detail View</div>
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {acoRecords.map(r => (
                    <button key={r.performance_year}
                      onClick={() => setDetailYear(r.performance_year)}
                      style={{ padding: '4px 11px', borderRadius: 20, border: '1.5px solid',
                        borderColor: detailYear === r.performance_year ? 'var(--teal)' : 'rgba(255,255,255,0.3)',
                        background: detailYear === r.performance_year ? 'var(--teal)' : 'transparent',
                        color: detailYear === r.performance_year ? 'var(--navy)' : 'rgba(255,255,255,0.8)',
                        cursor: 'pointer', fontSize: 12, fontFamily: 'var(--font)',
                        fontWeight: detailYear === r.performance_year ? 700 : 400 }}>
                      {r.performance_year}
                    </button>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.45)', marginLeft: 'auto' }}>
                  Spend breakdown · CAHPS · Clinical quality · Provider composition
                </div>
              </div>
            </div>

{/* ── Provider composition ── */}
      {latest && (
        <div className="chart-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
            <div className="chart-title">Provider Composition</div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {acoRecords.map(r => (
                <button key={r.performance_year}
                  onClick={() => setDetailYear(r.performance_year)}
                  style={{ padding: '4px 10px', borderRadius: 16, border: '1.5px solid',
                    borderColor: detailYear === r.performance_year ? 'var(--teal)' : 'var(--gray-300)',
                    background: detailYear === r.performance_year ? 'var(--teal-light)' : 'white',
                    cursor: 'pointer', fontSize: 12, fontFamily: 'var(--font)',
                    color: detailYear === r.performance_year ? 'var(--teal-dark)' : 'var(--gray-600)',
                    fontWeight: detailYear === r.performance_year ? 600 : 400 }}>
                  {r.performance_year}
                </button>
              ))}
            </div>
          </div>
          {(() => {
            const rec = acoRecords.find(r => r.performance_year === detailYear) || latest;
            return (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {[
                  { label: 'PCPs',        value: rec.n_pcp },
                  { label: 'Specialists', value: rec.n_spec },
                  { label: 'NPs',         value: rec.n_np },
                  { label: 'PAs',         value: rec.n_pa },
                  { label: 'Hospitals',   value: rec.n_hosp },
                  { label: 'FQHCs',       value: rec.n_fqhc },
                  { label: 'RHCs',        value: rec.n_rhc },
                  { label: 'CAHs',        value: rec.n_cah },
                ].filter(p => p.value != null && p.value > 0).map(p => (
                  <div key={p.label} style={{ background: 'var(--blue-light)', borderRadius: 'var(--radius)',
                    padding: '8px 14px', textAlign: 'center', minWidth: 80 }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--navy)' }}>{fmt.num(p.value)}</div>
                    <div style={{ fontSize: 11, color: 'var(--blue)', marginTop: 2 }}>{p.label}</div>
                  </div>
                ))}
              </div>
            );
          })()}
        </div>
      )}

{/* ── Spend category breakdown ── */}
      {latest && (
        <div className="chart-card">
          <div className="chart-title">Spend Category Breakdown — PY {detailYear || latest?.performance_year}</div>
          <div className="chart-sub">Annualized payments per assigned beneficiary year — ACO vs. program average</div>
          {mixData.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
              {mixData.sort((a,b) => b.acoVal - a.acoVal).map(cat => (
                <div key={cat.name} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 86, fontSize: 12, color: 'var(--gray-600)', textAlign: 'right', flexShrink: 0 }}>{cat.name}</div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 28, fontSize: 11, color: 'var(--gray-400)' }}>ACO</div>
                      <div style={{ flex: 1, background: 'var(--gray-100)', borderRadius: 3, height: 14 }}>
                        <div style={{ width: `${cat['ACO %']}%`, height: '100%', background: COLORS.teal, borderRadius: 3 }} />
                      </div>
                      <div style={{ width: 80, fontSize: 11, fontWeight: 600, color: 'var(--navy)', textAlign: 'right', flexShrink: 0 }}>
                        ${fmt.num(cat.acoVal)} ({cat['ACO %']}%)
                      </div>
                    </div>
                    {cat.progVal != null && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 28, fontSize: 11, color: 'var(--gray-400)' }}>Prog</div>
                        <div style={{ flex: 1, background: 'var(--gray-100)', borderRadius: 3, height: 14 }}>
                          <div style={{ width: `${cat['Prog %']}%`, height: '100%', background: COLORS.gray, borderRadius: 3 }} />
                        </div>
                        <div style={{ width: 80, fontSize: 11, color: 'var(--gray-500)', textAlign: 'right', flexShrink: 0 }}>
                          ${fmt.num(cat.progVal)} ({cat['Prog %']}%)
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: 'var(--gray-400)', fontSize: 13, padding: 16 }}>Spend breakdown not available.</div>
          )}
        </div>
      )}

{/* ── CAHPS quality comparison ── */}
      {qualData.length > 0 && (
        <div className="chart-card">
          <div className="chart-title">CAHPS Patient Experience — ACO vs. Program Average · PY {detailYear || latest?.performance_year}</div>
          <div className="chart-sub">Scores on 0–100 scale · higher = better</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
            {qualData.sort((a,b) => (b.aco||0) - (a.aco||0)).map(d => (
              <div key={d.label} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 200, fontSize: 12, color: 'var(--gray-700)', flexShrink: 0, textAlign: 'right' }}
                  title={d.fullLabel}>{d.label}</div>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {d.aco != null && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 28, fontSize: 11, color: 'var(--teal-dark)', fontWeight: 600 }}>ACO</div>
                      <div style={{ flex: 1, background: 'var(--gray-100)', borderRadius: 3, height: 14 }}>
                        <div style={{ width: `${d.aco}%`, height: '100%', borderRadius: 3,
                          background: d.aco >= 85 ? COLORS.teal : d.aco >= 70 ? COLORS.blue : COLORS.coral }} />
                      </div>
                      <div style={{ width: 36, fontSize: 12, fontWeight: 700, color: 'var(--navy)', textAlign: 'right', flexShrink: 0 }}>{d.aco.toFixed(1)}</div>
                    </div>
                  )}
                  {d.prog != null && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ width: 28, fontSize: 11, color: 'var(--gray-400)' }}>Prog</div>
                      <div style={{ flex: 1, background: 'var(--gray-100)', borderRadius: 3, height: 14 }}>
                        <div style={{ width: `${d.prog}%`, height: '100%', borderRadius: 3, background: COLORS.gray }} />
                      </div>
                      <div style={{ width: 36, fontSize: 12, color: 'var(--gray-500)', textAlign: 'right', flexShrink: 0 }}>{d.prog.toFixed(1)}</div>
                    </div>
                  )}
                </div>
                {d.aco != null && d.prog != null && (
                  <div style={{ width: 44, fontSize: 11, fontWeight: 600, textAlign: 'right', flexShrink: 0,
                    color: d.aco >= d.prog ? 'var(--green)' : 'var(--red)' }}>
                    {d.aco >= d.prog ? '+' : ''}{(d.aco - d.prog).toFixed(1)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

{/* ── Clinical Quality Measures ACO vs Program ── */}
      {clinicalQualData.length > 0 && (() => {
        const yr = clinicalQualData[0]?.year;
        return (
          <div className="chart-card">
            <div className="chart-title">Clinical Quality Measures — ACO vs. Program Average · PY {yr}</div>
            <div className="chart-sub">
              Individual measure performance vs. program average · ↑ above avg · ↓ below avg
              {yr < (latest?.performance_year || 0) && (
                <span style={{ marginLeft: 8, color: 'var(--amber)', fontWeight: 500 }}>
                  (most recent year with clinical measure data)
                </span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 12 }}>
              {clinicalQualData.sort((a,b) => {
                const aDiff = a.aco != null && a.prog != null ? (a.higher ? a.aco - a.prog : a.prog - a.aco) : -999;
                const bDiff = b.aco != null && b.prog != null ? (b.higher ? b.aco - b.prog : b.prog - b.aco) : -999;
                return bDiff - aDiff;
              }).map(d => (
                <div key={d.field} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 190, fontSize: 12, color: 'var(--gray-700)', flexShrink: 0, textAlign: 'right' }}>
                    {d.label}
                    {!d.higher && <span style={{ fontSize: 10, color: 'var(--amber)', marginLeft: 4 }}>↓ lower=better</span>}
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {d.aco != null && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 28, fontSize: 11, color: 'var(--teal-dark)', fontWeight: 600 }}>ACO</div>
                        <div style={{ flex: 1, background: 'var(--gray-100)', borderRadius: 3, height: 14 }}>
                          <div style={{ width: `${Math.min(100, d.aco)}%`, height: '100%', borderRadius: 3,
                            background: d.prog == null ? COLORS.teal :
                              (d.higher ? (d.aco >= d.prog ? COLORS.teal : COLORS.coral) :
                                         (d.aco <= d.prog ? COLORS.teal : COLORS.coral)) }} />
                        </div>
                        <div style={{ width: 38, fontSize: 12, fontWeight: 700, color: 'var(--navy)', textAlign: 'right', flexShrink: 0 }}>{d.aco.toFixed(1)}</div>
                      </div>
                    )}
                    {d.prog != null && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <div style={{ width: 28, fontSize: 11, color: 'var(--gray-400)' }}>Avg</div>
                        <div style={{ flex: 1, background: 'var(--gray-100)', borderRadius: 3, height: 14 }}>
                          <div style={{ width: `${Math.min(100, d.prog)}%`, height: '100%', borderRadius: 3, background: COLORS.gray }} />
                        </div>
                        <div style={{ width: 38, fontSize: 12, color: 'var(--gray-500)', textAlign: 'right', flexShrink: 0 }}>{d.prog.toFixed(1)}</div>
                      </div>
                    )}
                  </div>
                  {d.aco != null && d.prog != null && (() => {
                    const diff = d.higher ? d.aco - d.prog : d.prog - d.aco;
                    return (
                      <div style={{ width: 44, fontSize: 11, fontWeight: 600, textAlign: 'right', flexShrink: 0,
                        color: diff >= 0 ? 'var(--green)' : 'var(--red)' }}>
                        {diff >= 0 ? '+' : ''}{diff.toFixed(1)}
                      </div>
                    );
                  })()}
                </div>
              ))}
            </div>
            <div style={{ marginTop: 10, fontSize: 11, color: 'var(--gray-400)' }}>
              Green = above program average (or better for inverse measures) · Red = below average · Clinical measures available PY 2021–2023
            </div>
          </div>
        );
      })()}
          </>
        );
      })()}

      {/* ── Year-by-year summary table ── */}
      <div className="chart-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <div className="chart-title">Year-by-Year Summary</div>
          <button
            onClick={() => {
              const headers = ['Year','Track','N Benes','Savings Rate','Earned Savings','Gen Savings','Quality Score','Expense PBPM','Composite RAF'];
              const rows = acoRecords.map(r => [
                r.performance_year, r.current_track || '',
                r.n_ab, r.sav_rate != null ? (r.sav_rate*100).toFixed(2)+'%' : '',
                r.earn_save_loss != null ? r.earn_save_loss.toFixed(0) : '',
                r.gen_save_loss  != null ? r.gen_save_loss.toFixed(0)  : '',
                r.qual_score     != null ? r.qual_score.toFixed(1)     : '',
                r.expense_pbpm   != null ? r.expense_pbpm.toFixed(2)   : '',
                r.composite_raf_py != null ? r.composite_raf_py.toFixed(3) : '',
              ]);
              const csv = [headers,...rows].map(r => r.map(v=>`"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
              const blob = new Blob([csv],{type:'text/csv'});
              const url  = URL.createObjectURL(blob);
              const a    = document.createElement('a');
              a.href = url; a.download = `${selectedACO?.aco_id}-history.csv`; a.click();
              URL.revokeObjectURL(url);
            }}
            style={{ padding: '5px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--teal)',
              background: 'var(--teal-light)', color: 'var(--teal-dark)', cursor: 'pointer',
              fontSize: 12, fontFamily: 'var(--font)', fontWeight: 600 }}>
            ↓ Download CSV
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'var(--gray-50)', borderBottom: '2px solid var(--gray-200)' }}>
                {['Year','Track','N Benes','Savings Rate','Earned Savings','Gen Savings','Quality','Expense PBPM','RAF'].map(h => (
                  <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600,
                    color: 'var(--gray-600)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {acoRecords.map((r, i) => (
                <tr key={r.performance_year}
                  style={{ background: i % 2 === 1 ? 'var(--gray-50)' : 'white', borderBottom: '1px solid var(--gray-100)' }}>
                  <td style={{ padding: '7px 10px', fontWeight: 600, color: 'var(--navy)' }}>{r.performance_year}</td>
                  <td style={{ padding: '7px 10px', color: 'var(--gray-600)', fontSize: 12 }}>{r.current_track || '—'}</td>
                  <td style={{ padding: '7px 10px' }}>{fmt.num(r.n_ab)}</td>
                  <td style={{ padding: '7px 10px', fontWeight: 600,
                    color: r.sav_rate > 0 ? 'var(--green)' : r.sav_rate < 0 ? 'var(--red)' : 'var(--gray-600)' }}>
                    {fmt.pct(r.sav_rate)}
                  </td>
                  <td style={{ padding: '7px 10px', fontWeight: 600,
                    color: r.earn_save_loss > 0 ? 'var(--green)' : r.earn_save_loss < 0 ? 'var(--red)' : 'var(--gray-600)' }}>
                    {fmt.dollars(r.earn_save_loss, 2)}
                  </td>
                  <td style={{ padding: '7px 10px' }}>{fmt.dollars(r.gen_save_loss, 2)}</td>
                  <td style={{ padding: '7px 10px' }}>{r.qual_score?.toFixed(1) ?? '—'}</td>
                  <td style={{ padding: '7px 10px' }}>
                    {r.expense_pbpm != null ? `$${Number(r.expense_pbpm).toLocaleString('en-US',{minimumFractionDigits:2,maximumFractionDigits:2})}` : '—'}
                  </td>
                  <td style={{ padding: '7px 10px' }}>{r.composite_raf_py?.toFixed(3) ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
}
